import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import { useMarketingActivity, coverImg, draftComplete, fmtBaht, freqText, isUrgent, kindInfo, leftText, rewardInfo, stateInfo, turnPct, whenText } from "../marketingActivityLogic";
import { Empty, LoginGate, Skeleton } from "../ui";

/*
 * หน้า "กิจกรรม" — ดีไซน์ landing-ver2 (แดชบอร์ดภารกิจนีออน): แผงสถิติเรืองแสง · ภารกิจเป็นแถวกางออกในหน้า (อ่าน/ดู → ตอบ → รับ) · ประวัติแบบล็อก
 * (ตรรกะอยู่ที่ marketingActivityLogic.js ร่วมกับ landing ซึ่งใช้ดีไซน์บอร์ดใบปลิวแทน)
 */

const safeHref = (u) => (/^https?:\/\//i.test(String(u || "")) ? u : "#");

function Done({ result, onClose }) {
  const closeRef = useRef(null);
  const show = result && result.kind !== "wrong";
  useEffect(() => {
    if (!show) return undefined;
    if (closeRef.current) closeRef.current.focus();
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [show, onClose]);
  if (!show) return null;
  const win = result.kind === "win";
  const r = win ? result.reward : null;
  const info = r ? rewardInfo({ reward_type: r.type, reward_amount: r.amount }) : null;
  return (
    <div className="acn-overlay" role="dialog" aria-modal="true" aria-label={win ? "ตอบถูกต้อง" : "ส่งคำตอบไม่สำเร็จ"} onClick={onClose}>
      <div className="acn-done" onClick={(e) => e.stopPropagation()}>
        {win ? (
          <>
            <Icon icon="fluent-emoji-flat:trophy" width={88} height={88} className="acn-done-ic" />
            <div className="acn-done-kicker">
              <Icon icon="solar:check-circle-bold" width={16} /> MISSION COMPLETE
            </div>
            <p className="acn-done-title">{result.title}</p>
            {r && r.distributed ? <div className="acn-done-amt">+{info.text}</div> : <p>ขอบคุณที่ร่วมกิจกรรม</p>}
            <ul>
              {r && r.turnover_required > 0 && (
                <li>
                  <Icon icon="solar:lock-keyhole-minimalistic-bold" width={16} /> ต้องแทงให้ครบ {fmtBaht(r.turnover_required)} ก่อนถอนเงินรางวัลส่วนนี้
                </li>
              )}
              {r && r.play_label && (
                <li>
                  <Icon icon="solar:ticket-bold" width={16} /> เงินรางวัลนี้แทงได้{r.play_label}
                </li>
              )}
            </ul>
          </>
        ) : (
          <>
            <Icon icon="fluent-emoji-flat:locked" width={72} height={72} />
            <div className="acn-done-kicker err">ส่งไม่สำเร็จ</div>
            <p>{result.message}</p>
          </>
        )}
        <button type="button" ref={closeRef} className="acn-btn" onClick={onClose}>
          ตกลง
        </button>
      </div>
    </div>
  );
}

function Mission({ a, expanded, onToggle, draft, setDraft, busy, message, onSubmit }) {
  const st = stateInfo(a);
  const k = kindInfo(a.kind);
  const rw = rewardInfo(a);
  const left = leftText(a);
  const qs = a.questions || [];
  const ready = draftComplete(qs, draft);
  const hasLinks = a.links.length > 0;
  return (
    <article className={`acn-mission k-${a.kind}${expanded ? " open" : ""}${st.canDo ? "" : " is-done"}`}>
      <button type="button" className="acn-m-head" aria-expanded={expanded} disabled={!st.canDo} onClick={onToggle}>
        <span className="acn-m-ic">
          {a.cover_image ? <img src={coverImg(a.cover_image)} alt="" /> : <Icon icon={k.icon} width={30} height={30} />}
        </span>
        <span className="acn-m-main">
          <b>{a.title}</b>
          <small>
            {k.label} · {freqText(a)}
            {left && <em className={isUrgent(a) ? "urgent" : ""}> · {left}</em>}
            {st.canDo && a.attempts_left !== null && a.attempts_left !== undefined && ` · ตอบผิดได้อีก ${a.attempts_left}`}
            {st.canDo && a.quota_left !== null && a.quota_left !== undefined && ` · เหลือ ${a.quota_left} สิทธิ์`}
          </small>
        </span>
        {st.canDo ? (
          <span className={`acn-m-rw${rw.cash ? " cash" : ""}`}>
            <Icon icon={rw.icon} width={20} height={20} />
            {rw.text}
          </span>
        ) : (
          <span className={`acn-chip ${st.tone}`}>{st.label}</span>
        )}
        {st.canDo && <Icon icon="solar:alt-arrow-down-bold" width={18} className="acn-chev" />}
      </button>

      {expanded && st.canDo && (
        <div className="acn-m-body">
          {a.description && <p className="acn-desc">{a.description}</p>}
          <ol className="acn-steps">
            {hasLinks && (
              <li>
                <span className="acn-node">1</span>
                <div className="acn-step">
                  <h4>{k.verb}</h4>
                  <div className="acn-links">
                    {a.links.map((l) => (
                      <a key={l.url} className="acn-link" href={safeHref(l.url)} target="_blank" rel="noopener noreferrer">
                        <Icon icon={a.kind === "VIDEO" ? "solar:play-circle-bold" : "solar:link-round-bold"} width={18} />
                        {l.label}
                        <Icon icon="solar:arrow-right-up-linear" width={14} />
                      </a>
                    ))}
                  </div>
                </div>
              </li>
            )}
            {qs.length > 0 && (
              <li>
                <span className="acn-node">{hasLinks ? 2 : 1}</span>
                <div className="acn-step">
                  <h4>ตอบคำถามให้ถูกทุกข้อ</h4>
                  <div className="acn-qs">
                    {qs.map((q, i) => (
                      <label key={q.id} className="acn-q">
                        <span>
                          {i + 1}. {q.question}
                        </span>
                        <input
                          value={draft[q.id] || ""}
                          maxLength={255}
                          autoComplete="off"
                          onChange={(e) => setDraft({ ...draft, [q.id]: e.target.value })}
                          aria-label={`คำตอบข้อ ${i + 1}`}
                        />
                      </label>
                    ))}
                  </div>
                </div>
              </li>
            )}
            <li>
              <span className="acn-node">{(hasLinks ? 1 : 0) + (qs.length ? 1 : 0) + 1}</span>
              <div className="acn-step">
                <h4>รับรางวัล {rw.text}</h4>
                {message && (
                  <div className="acn-wrong" role="alert">
                    <Icon icon="solar:danger-triangle-bold" width={18} />
                    <span>
                      <b>{message.message}</b>
                      {message.kind === "wrong" && message.attempts_left !== null && message.attempts_left !== undefined ? ` · ตอบผิดได้อีก ${message.attempts_left} ครั้ง` : message.kind === "wrong" ? " · ตรวจคำตอบแล้วลองใหม่ได้เลย" : ""}
                    </span>
                  </div>
                )}
                <button type="button" className="acn-btn" disabled={!ready || busy} onClick={onSubmit}>
                  <Icon icon={busy ? "svg-spinners:ring-resize" : "solar:plain-bold"} width={18} />
                  {busy ? "กำลังตรวจคำตอบ..." : qs.length ? "ส่งคำตอบรับรางวัล" : "ยืนยันรับรางวัล"}
                </button>
              </div>
            </li>
          </ol>
          <ul className="acn-rules">
            {(a.rules || []).map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
        </div>
      )}
    </article>
  );
}

export default function MarketingActivityPage({ onDone }) {
  const nav = useNavigate();
  const ma = useMarketingActivity(onDone);
  const { view } = ma;
  const [openId, setOpenId] = useState(null);
  const [draft, setDraft] = useState({});

  useEffect(() => {
    setDraft({});
  }, [openId]);

  if (!ma.isAuthenticated) {
    return (
      <LoginGate
        icon="solar:flag-bold-duotone"
        title="เข้าสู่ระบบเพื่อร่วมกิจกรรม"
        desc="อ่านบทความ ดูคลิป ตอบคำถามให้ถูก แล้วรับรางวัลเป็นเพชรหรือเงินสด"
        perks={["รางวัลเข้ากระเป๋าทันทีที่ตอบถูก", "มีกิจกรรมใหม่เรื่อย ๆ", "ดูประวัติการรับรางวัล"]}
        onLogin={() => nav("/auth")}
      />
    );
  }
  if (ma.loading) return <Skeleton h={320} style={{ marginTop: 18 }} />;

  const open = view.activities.filter((a) => stateInfo(a).canDo);
  const rest = view.activities.filter((a) => !stateInfo(a).canDo);
  const inline = ma.result && ma.result.kind !== "win" ? ma.result : null;
  const edit = (d) => {
    ma.closeResult();
    setDraft(d);
  };
  const toggle = (id) => {
    ma.closeResult();
    setOpenId(openId === id ? null : id);
  };
  const submit = async (a) => {
    ma.closeResult();
    const ok = await ma.submit(a, draft);
    if (ok) setOpenId(null);
  };

  return (
    <div className="acn-page">
      <section className={`acn-hero${ma.enabled ? "" : " is-off"}`}>
        <div className="acn-glow" aria-hidden="true" />
        <div className="acn-head">
          <span className="acn-head-ic">
            <Icon icon="fluent-emoji-flat:megaphone" width={34} height={34} />
          </span>
          <div>
            <h1>กิจกรรม</h1>
            <p>อ่าน · ดู · ตอบคำถาม รับรางวัล</p>
          </div>
          <span className={`acn-led ${ma.enabled ? "on" : "off"}`}>{ma.enabled ? "ON" : "OFF"}</span>
        </div>
        {ma.enabled ? (
          <div className="acn-metrics">
            <div>
              <b>{open.length}</b>
              <span>ภารกิจที่ทำได้</span>
            </div>
            <div>
              <b>{view.totals.count}</b>
              <span>ครั้งที่ได้รับ</span>
            </div>
            <div>
              <b>{fmtBaht(view.totals.cash)}</b>
              <span>เงินสดสะสม</span>
            </div>
            <div>
              <b>{view.totals.diamonds}</b>
              <span>เพชรสะสม</span>
            </div>
          </div>
        ) : (
          <div className="acn-off">
            <Icon icon="solar:lock-keyhole-minimalistic-bold" width={18} /> ระบบกิจกรรมปิดให้บริการชั่วคราว
          </div>
        )}
      </section>

      {ma.enabled && (
        <section>
          <h2 className="acn-h2">
            <Icon icon="solar:target-bold-duotone" width={22} /> ภารกิจที่เปิดอยู่
          </h2>
          {open.length === 0 ? (
            <div className="acn-card">
              <Empty icon="solar:flag-bold-duotone">{view.activities.length ? "คุณทำครบทุกภารกิจแล้ว — รอภารกิจใหม่เร็ว ๆ นี้" : "ยังไม่มีกิจกรรมในตอนนี้ — กลับมาดูใหม่นะ"}</Empty>
            </div>
          ) : (
            <div className="acn-list">
              {open.map((a) => (
                <Mission
                  key={a.id}
                  a={a}
                  expanded={openId === a.id}
                  onToggle={() => toggle(a.id)}
                  draft={openId === a.id ? draft : {}}
                  setDraft={edit}
                  busy={ma.busyId === a.id}
                  message={openId === a.id ? inline : null}
                  onSubmit={() => submit(a)}
                />
              ))}
            </div>
          )}
        </section>
      )}

      {ma.enabled && rest.length > 0 && (
        <section>
          <h2 className="acn-h2">
            <Icon icon="solar:check-square-bold-duotone" width={22} /> เสร็จแล้ว / ปิดรับ
          </h2>
          <div className="acn-list">
            {rest.map((a) => (
              <Mission key={a.id} a={a} expanded={false} onToggle={() => {}} draft={{}} setDraft={edit} busy={false} message={null} onSubmit={() => {}} />
            ))}
          </div>
        </section>
      )}

      {view.active.length > 0 && (
        <section>
          <h2 className="acn-h2">
            <Icon icon="solar:lock-keyhole-minimalistic-bold-duotone" width={22} /> รางวัลที่ยังมีเงื่อนไข
          </h2>
          <div className="acn-list">
            {view.active.map((h) => (
              <div className="acn-cond" key={h.id}>
                <div className="acn-cond-top">
                  <b>{h.title}</b>
                  <strong>{fmtBaht(h.amount)}</strong>
                </div>
                {h.locked && (
                  <>
                    <div className="acn-bar">
                      <i style={{ width: `${turnPct(h)}%` }} />
                    </div>
                    <small>
                      ทำเทิร์น {fmtBaht(h.turnover_done)} / {fmtBaht(h.turnover_required)}
                    </small>
                  </>
                )}
                {h.restricted_left > 0 && (
                  <small>
                    เหลือ {fmtBaht(h.restricted_left)} · {h.play_label}
                  </small>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      <section>
        <h2 className="acn-h2">
          <Icon icon="solar:history-bold-duotone" width={22} /> ประวัติการรับรางวัล
        </h2>
        {view.history.length === 0 ? (
          <div className="acn-card">
            <Empty icon="solar:flag-bold-duotone">ยังไม่เคยรับรางวัลจากกิจกรรม</Empty>
          </div>
        ) : (
          <ul className="acn-log">
            {view.history.map((h) => {
              const r = rewardInfo({ reward_type: h.reward_type, reward_amount: h.amount });
              return (
                <li key={h.id}>
                  <time>{whenText(h.received_at)}</time>
                  <span className="acn-log-t">{h.title || "กิจกรรม"}</span>
                  <strong className={r.cash ? "cash" : ""}>+{r.text}</strong>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <Done result={ma.result} onClose={ma.closeResult} />
    </div>
  );
}
